// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions, {
    ProductActionsProps,
    ProductActionsStyle,
} from "@vanmeter/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@vanmeter/App/Components/Product/ProductGridContainer";
import QtyNumbers, { QtyNumbersStyles } from "@vanmeter/App/Components/Product/QtyNumbers";
import { TooltipPresentationProps } from "@vanmeter/MobiusOverrides/Tooltip/Tooltip";
import { ProductAvailabilityStyles } from "@vanmeter/Overrides/Components/ProductAvailability";
import ProductPrice, { ProductPriceStyles } from "@vanmeter/Overrides/Components/ProductPrice";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@vanmeter/Overrides/Widgets/ProductList/ProductListProductImage";
import { mergeWithCss } from "@vanmeter/XDL/Utilities";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface ProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showImageBadges?: boolean;
    showAddToCartActions?: boolean;
    showCompare?: boolean;
    showInfo?: boolean;
    maximumNumberOfImageBadges?: number;
    hideAvailabilityLink?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
    showAddToCartIcon?: boolean;
    containerClassName?: string;
    isPdp?: boolean;
    onChangeCompare?: (checked: boolean) => void;
    goToPageLink?: string;
    goToPageLabel?: string;
    showAddToCartDivider?: boolean;
}
const mapStateToProps = (state: ApplicationState) => ({
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    isAuthenticated: state.context.session.isAuthenticated,
    location: getLocation(state),
});

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

type Props = ProductPriceAndActionsProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productActionsStyles?: ProductActionsStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListStyles?: ProductAddToListLinkStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
    priceAvailabilityLink?: LinkPresentationProps;
    imageBadgesStyles?: ProductImageBadgesStyles;
    priceBadgesContainer?: InjectableCss;
    loginActionButton?: ButtonProps;
    goToPage?: LinkPresentationProps;
    addPackTooltip?: TooltipPresentationProps;
    addPackButton?: ButtonPresentationProps;
    qtyNumbersStyles?: QtyNumbersStyles;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    showImageBadges,
    extendedStyles,
    showAddToCartActions = true,
    showAddToCartIcon = false,
    showCompare = false,
    hideAvailabilityLink = false,
    showInfo = true,
    maximumNumberOfImageBadges = 5,
    productContext,
    canSeePrices,
    signInUrl,
    onAddToCartClick,
    showDisabled,
    isAuthenticated,
    containerClassName,
    isPdp = false,
    onChangeCompare,
    goToPageLabel,
    goToPageLink,
    location,
    addToCart,
    showAddToCartDivider,
    ...productActionsProps
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));

    const showAvailabilityBlock =
        (canSeePrices && showAvailability) || (isPdp && !productContext.product.isVariantParent);

    return (
        <ProductGridContainer
            data-test-selector="product-actions-grid-template"
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
        >
            {showPrice && (
                <ProductGridContainer.Price>
                    <ProductPrice
                        product={productContext}
                        showInvalidPriceMessage={true}
                        showLabel={false}
                        showSalePriceLabel={isPdp}
                        showUnitOfMeasure={true}
                        isProductDetailsPage={isPdp}
                        extendedStyles={styles.productPriceStyles}
                        showSavings={true}
                    />
                </ProductGridContainer.Price>
            )}

            {showAvailabilityBlock && (
                <ProductGridContainer.Availability>
                    {canSeePrices && showAvailability && (
                        <ProductContextAvailability
                            extendedStyles={styles.productAvailabilityStyles}
                            isProductDetailsPage={isPdp}
                        />
                    )}
                    {isPdp && !productContext.product.isVariantParent && (
                        <QtyNumbers
                            minimumOrderQty={Number(productContext.product.minimumOrderQty) || 1}
                            extendedStyles={styles.qtyNumbersStyles}
                        />
                    )}
                </ProductGridContainer.Availability>
            )}

            {showAddToCartActions && (
                <ProductGridContainer.AddToCartActions {...{ showDivider: showAddToCartDivider }}>
                    <ProductActions
                        {...productActionsProps}
                        onAddToCartClick={onAddToCartClick}
                        showDisabled={showDisabled || productContext.product.cantBuy}
                        iconOnly={showAddToCartIcon}
                        extendedStyles={styles.productActionsStyles}
                        isPdp={isPdp}
                    />
                </ProductGridContainer.AddToCartActions>
            )}
            {canSeePrices && (showAddToList || showCompare) && (
                <ProductGridContainer.AddToList>
                    {showCompare && (
                        <ProductListProductImage
                            showImage={false}
                            showCompare={showCompare}
                            extendedStyles={styles.productListProductImageStyles}
                        />
                    )}
                    {showAddToList ? (
                        <ProductAddToListLink
                            {...({ nonEmpty: true, large: true } as any)}
                            extendedStyles={styles.productAddToListStyles}
                        />
                    ) : (
                        <div />
                    )}
                </ProductGridContainer.AddToList>
            )}
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductPriceAndActions));
